import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  HStack,
  Select,
  Flex,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const ROWS_PER_PAGE = 100;

const DataPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const dataset_id = localStorage.getItem('dataset_id');
      setIsLoading(true);
      // First get the list of cached data
      const listResponse = await axios.post(`${API_URL}/api/scripts/cache/list`, {
        dataset_id: dataset_id
      });
      
      if (listResponse.data.states && listResponse.data.states.length > 0) {
        // Get the first cache key
        const firstCacheKey = listResponse.data.states[0].key;
        
        // Get the data for that cache key
        const dataResponse = await axios.post(`${API_URL}/api/scripts/cache/get`, {
          cache_key: firstCacheKey
        });
        
        // Access the complete_data from the correct path and parse it
        const completeData = dataResponse.data.data.complete_data;
        
        if (completeData) {
          try {
            // Parse the stringified JSON data
            const parsedData = JSON.parse(completeData);
            
            if (Array.isArray(parsedData)) {
              setData(parsedData);
              
              // Extract columns from the first row
              if (parsedData.length > 0) {
                setColumns(Object.keys(parsedData[0]));
              }
              
              // Calculate total pages
              setTotalPages(Math.ceil(parsedData.length / ROWS_PER_PAGE));
            } else {
              toast({
                title: 'Error',
                description: 'Invalid data format: data is not an array',
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
            }
          } catch (parseError) {
            toast({
              title: 'Error',
              description: 'Failed to parse data',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: 'Error',
            description: 'No data found in response',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentPageData = () => {
    if (!data) return [];
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return data.slice(startIndex, endIndex);
  };

  const handlePageChange = (e) => {
    setCurrentPage(Number(e.target.value));
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box p={6} borderRadius="lg" boxShadow="md">
        <HStack justify="space-between" align="center" mb={6}>
          <Heading>Data</Heading>
          <Button onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </Button>
        </HStack>

        {isLoading ? (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" />
            <Text mt={4}>Cargando datos...</Text>
          </Box>
        ) : !data ? (
          <Text textAlign="center" py={8}>
            No hay datos disponibles
          </Text>
        ) : (
          <Box>
            <Box p={4} bg="blue.50" borderRadius="md" mb={4}>
              <Text fontWeight="bold">Data Summary:</Text>
              <Text>Rows: {data.length}</Text>
              <Text>Columns: {Array.isArray(columns) ? columns.join(', ') : 'N/A'}</Text>
            </Box>

            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    {columns.map((column) => (
                      <Th key={column}>{column}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {getCurrentPageData().map((row, rowIndex) => (
                    <Tr key={rowIndex}>
                      {columns.map((column) => (
                        <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination Controls */}
            <Flex justify="center" align="center" mt={4} gap={2}>
              <Button
                onClick={() => setCurrentPage(1)}
                isDisabled={currentPage === 1}
                size="sm"
              >
                First
              </Button>
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                isDisabled={currentPage === 1}
                size="sm"
              >
                Previous
              </Button>
              <Text mx={2}>
                Page {currentPage} of {totalPages}
              </Text>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                isDisabled={currentPage === totalPages}
                size="sm"
              >
                Next
              </Button>
              <Button
                onClick={() => setCurrentPage(totalPages)}
                isDisabled={currentPage === totalPages}
                size="sm"
              >
                Last
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default DataPage; 